import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigate, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import TrovaBrand from '../components/Auth/TrovaBrand';
import { useAuth } from '../contexts/AuthContext';
import { trackEvent } from '../utils/analytics';

const AuthCard = styled.div`
  width: 100%;
  max-width: 440px;
  padding: 32px;
  background: var(--white);
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
`;

const Field = styled.div`
  margin: 18px 0;

  label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    color: var(--color-text-secondary);
  }

  input {
    width: 100%;
    padding: 12px;
    border: 1px solid var(--medium-gray);
    border-radius: var(--border-radius);
    font-size: 16px;

    &:focus {
      outline: none;
      border-color: var(--primary-color);
    }
  }
`;

const Message = styled.p<{ $error?: boolean }>`
  margin: 14px 0;
  color: ${props => props.$error ? 'var(--danger-color)' : 'var(--color-text-secondary)'};
  font-size: 14px;
`;

const TextButton = styled.button`
  border: 0;
  padding: 0;
  background: transparent;
  color: var(--primary-color);
  font: inherit;
  cursor: pointer;
  text-decoration: underline;
`;

const FooterActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const FooterButton = styled.button<{ $primary?: boolean }>`
  flex: 1;
  min-height: 40px;
  padding: 8px 12px;
  border: 1px solid var(--color-primary);
  border-radius: 12px;
  background: ${props => props.$primary ? 'var(--color-primary)' : 'transparent'};
  color: ${props => props.$primary ? 'var(--color-bg)' : 'var(--color-primary)'};
  font-family: var(--font-ui);
  font-size: 1rem;
  font-weight: var(--font-weight-medium);
  cursor: pointer;

  &:hover {
    background: ${props => props.$primary ? 'var(--color-primary-hover)' : 'rgba(0, 218, 109, 0.1)'};
  }

  &:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 3px;
  }
`;

const VerifyEmailPage: React.FC = () => {
  const { user, loading, verifyEmailCode, resendVerificationCode, recoverVerification } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState(() => localStorage.getItem('pendingSignupEmail') || '');
  const [pendingId, setPendingId] = useState(() => localStorage.getItem('pendingSignupId') || '');
  const [code, setCode] = useState('');
  const [recoveryEmail, setRecoveryEmail] = useState(() => localStorage.getItem('pendingSignupEmail') || '');
  const [recoveryPassword, setRecoveryPassword] = useState('');
  const [showRecovery, setShowRecovery] = useState(() => !localStorage.getItem('pendingSignupId'));
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [resending, setResending] = useState(false);

  if (user && !loading) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleVerify = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!pendingId || code.length !== 6) return;

    setError('');
    setMessage('');
    try {
      await verifyEmailCode(pendingId, code);
      trackEvent('auth_signup_success', { method: 'password' });
      navigate('/dashboard', { replace: true });
    } catch {
      // Do not expose whether a pending signup or code exists.
      setError("That code couldn't be verified. Check it and try again, or request a new code.");
    }
  };

  const handleResend = async () => {
    if (!pendingId) return;
    setError('');
    setMessage('');
    setResending(true);
    try {
      await resendVerificationCode(pendingId);
      setMessage('A new code was sent. Check your inbox.');
      setCode('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send a new code right now. Please try again shortly.');
    } finally {
      setResending(false);
    }
  };

  const handleRecovery = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setMessage('');
    try {
      const normalizedEmail = recoveryEmail.trim().toLowerCase();
      const response = await recoverVerification(normalizedEmail, recoveryPassword);
      if (!response.pendingId) {
        throw new Error('Missing recovery challenge');
      }
      setEmail(normalizedEmail);
      setRecoveryEmail(normalizedEmail);
      setPendingId(response.pendingId);
      localStorage.setItem('pendingSignupEmail', normalizedEmail);
      localStorage.setItem('pendingSignupId', response.pendingId);
      setRecoveryPassword('');
      setCode('');
      setShowRecovery(false);
      setMessage('If those details match an unfinished signup, check your inbox. If you need a new code, wait a minute before resending.');
    } catch {
      // Keep recovery failures generic without disclosing pending accounts.
      setError("We couldn't process that request. Check your connection and try again.");
    }
  };

  return (
    <div className="login-page">
      <Helmet>
        <title>Verify your email</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="login-container">
        <div className="login-header">
          <TrovaBrand />
          <h2 className="login-title">{showRecovery ? 'Continue your signup' : 'Verify your email'}</h2>
          <p className="login-subtitle">
            {showRecovery
              ? 'Enter the email and password you used to start signup. Check your inbox for a code.'
              : `Enter the six-digit code sent to ${email || 'your email address'}.`}
          </p>
        </div>

        <AuthCard>
          {showRecovery ? (
            <form onSubmit={handleRecovery}>
              <Field>
                <label htmlFor="recovery-email">Signup email</label>
                <input
                  id="recovery-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={recoveryEmail}
                  onChange={event => setRecoveryEmail(event.target.value)}
                />
              </Field>
              <Field>
                <label htmlFor="recovery-password">Original signup password</label>
                <input
                  id="recovery-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={recoveryPassword}
                  onChange={event => setRecoveryPassword(event.target.value)}
                />
              </Field>
              {error && <Message $error role="alert">{error}</Message>}
              {message && <Message role="status">{message}</Message>}
              <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={loading}>
                {loading ? 'Checking…' : 'Send verification code'}
              </button>
              {pendingId && (
                <p style={{ textAlign: 'center', marginBottom: 0 }}>
                  <TextButton type="button" onClick={() => { setShowRecovery(false); setMessage(''); setError(''); }}>
                    I already have a code
                  </TextButton>
                </p>
              )}
            </form>
          ) : (
            <form onSubmit={handleVerify}>
              <Field>
                <label htmlFor="verification-code">Six-digit code</label>
                <input
                  id="verification-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                  value={code}
                  onChange={event => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                />
              </Field>
              {error && <Message $error role="alert">{error}</Message>}
              {message && <Message role="status">{message}</Message>}
              <button
                type="submit"
                className="btn btn-primary"
                style={{ width: '100%' }}
                disabled={loading || code.length !== 6}
              >
                {loading ? 'Verifying…' : 'Verify email'}
              </button>
              <p style={{ textAlign: 'center', marginBottom: 0 }}>
                Didn’t receive a code?{' '}
                <TextButton type="button" onClick={handleResend} disabled={loading || resending}>
                  {resending ? 'Sending…' : 'Resend code'}
                </TextButton>
              </p>
            </form>
          )}
          <FooterActions>
            <FooterButton type="button" onClick={() => navigate('/auth/login')}>Back</FooterButton>
            <FooterButton type="button" $primary onClick={() => navigate('/auth/register')}>New Signup</FooterButton>
          </FooterActions>
        </AuthCard>
      </div>
    </div>
  );
};

export default VerifyEmailPage;